import { useEffect, useCallback } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import api from '@/lib/axios';
import { getSocket } from '@/lib/socket';
import { useAuthStore } from '@/store/auth.store';

export interface Notificacion {
  id: string;
  tipo: 'SOLICITUD_RED' | 'REFERIDO_NUEVO' | 'REFERIDO_ACEPTADO' | 'REFERIDO_RECHAZADO' | 'CHAT_MENSAJE'
    | 'TAREA_ASIGNADA' | 'TAREA_MENCION' | 'TAREA_POR_VENCER' | 'TAREA_VENCIDA';
  titulo: string;
  cuerpo: string;
  leida: boolean;
  metadatos?: Record<string, any>;
  createdAt: string;
}

export function useNotificaciones() {
  const usuario = useAuthStore(s => s.usuario);
  const qc = useQueryClient();

  const { data: notificaciones = [], isLoading } = useQuery<Notificacion[]>({
    queryKey: ['notificaciones'],
    queryFn: () => api.get('/api/notificaciones').then(r => r.data),
    enabled: !!usuario,
    refetchInterval: 60_000,
  });

  const noLeidas = notificaciones.filter(n => !n.leida).length;

  // Escuchar nuevas notificaciones por WebSocket
  useEffect(() => {
    if (!usuario) return;
    const socket = getSocket();

    const handler = (notif: Notificacion) => {
      qc.setQueryData<Notificacion[]>(['notificaciones'], prev =>
        prev ? [notif, ...prev] : [notif],
      );
    };

    socket.on('notificacion:nueva', handler);
    return () => { socket.off('notificacion:nueva', handler); };
  }, [usuario, qc]);

  const marcarLeida = useCallback(async (id: string) => {
    await api.patch(`/api/notificaciones/${id}/leer`);
    qc.setQueryData<Notificacion[]>(['notificaciones'], prev =>
      prev?.map(n => n.id === id ? { ...n, leida: true } : n) ?? [],
    );
  }, [qc]);

  const marcarTodasLeidas = useCallback(async () => {
    await api.patch('/api/notificaciones/leer-todas');
    qc.setQueryData<Notificacion[]>(['notificaciones'], prev =>
      prev?.map(n => ({ ...n, leida: true })) ?? [],
    );
  }, [qc]);

  return { notificaciones, noLeidas, isLoading, marcarLeida, marcarTodasLeidas };
}
